import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/LanguageContext';
import { LOGO_LIGHT } from '../App';
import { Mail, Lock, Eye, EyeOff, Fingerprint, ScanFace, Phone } from 'lucide-react';
import axios from 'axios';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import { trackLogin } from '../lib/analytics';
import { biometric, isNative, browser } from '../lib/native';
import { Capacitor } from '@capacitor/core';

const API = API_URL;

// Splash image URL
const SPLASH_IMAGE = '/sfondo_login.webp';
// Desktop background image
const DESKTOP_BG_IMAGE = '/sfondo_login.webp';

export default function EmailLogin() {
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoginEnabled, setGoogleLoginEnabled] = useState(false);
  const [phoneLoginEnabled, setPhoneLoginEnabled] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('none');
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Handle keyboard show/hide for native apps
  useEffect(() => {
    if (!isNative) return;
    if (!Capacitor.isPluginAvailable('Keyboard')) return;
    
    let showListener = null;
    
    const setupKeyboard = async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
          setTimeout(() => {
            const activeElement = document.activeElement;
            if (activeElement && activeElement.tagName === 'INPUT') {
              activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        });
      } catch (e) {
        console.log('[Keyboard] Plugin not available:', e.message);
      }
    };
    
    setupKeyboard();
    
    return () => {
      if (showListener?.remove) showListener.remove();
    };
  }, []);

  // Check biometric availability on native
  useEffect(() => {
    const checkBiometric = async () => {
      if (!isNative) return;
      const result = await biometric.isAvailable();
      if (result.isAvailable) {
        setBiometricAvailable(true);
        setBiometricType(result.biometryType);
        // Auto-attempt biometric login if credentials are saved
        const creds = await biometric.getCredentials();
        if (creds) {
          handleBiometricLogin();
        }
      }
    };
    checkBiometric();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch public settings to check if Google login is enabled
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await httpClient.get(`${API}/api/settings/public`);
        // Respect the admin toggle on every platform. On native the OAuth
        // return is handled via the slimmy:// deep link (source=app).
        setGoogleLoginEnabled(!!response.data.google_login_enabled);
        // Phone-auth toggle: hide the "Accedi con telefono" fallback button
        // when admin disabled SMS auth (cost reduction).
        if (response.data?.phone_login_enabled === false) {
          setPhoneLoginEnabled(false);
        }
      } catch (err) {
        setGoogleLoginEnabled(false);
      }
    };
    fetchSettings();
  }, []);

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    setError('');
    let response;
    try {
      const authenticated = await biometric.authenticate(
        biometricType === 'faceId' ? 'Accedi con Face ID' : 'Accedi con impronta'
      );
      if (!authenticated) {
        setBiometricLoading(false);
        return;
      }
      const creds = await biometric.getCredentials();
      if (!creds) {
        setBiometricLoading(false);
        return;
      }
      response = await axios.post(`${API}/api/auth/login-email`, {
        email: creds.username,
        password: creds.password
      }, { withCredentials: true });
    } catch {
      setError('Accesso biometrico fallito. Usa email e password.');
      setBiometricLoading(false);
      return;
    }

    // Post-login side-effects — isolated so iOS WebKit quirks
    // (e.g. "The object can not be cloned.") can't strand the user.
    try {
      if (response?.data?.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
    } catch (_) { /* ignore */ }

    const userData = response?.data?.user;
    try { trackLogin('biometric'); } catch (_) { /* ignore */ }

    try {
      if (userData?.is_admin) {
        localStorage.setItem('pending_admin_redirect', 'true');
        setUser(userData);
        setTimeout(() => {
          try { window.location.replace('/admin'); } catch (_) {}
        }, 100);
      } else {
        setUser(userData);
        const returnUrl = searchParams.get('returnUrl') || '/app/dashboard';
        navigate(returnUrl, { replace: true });
      }
    } catch (_) {
      try { window.location.replace('/app/dashboard'); } catch (_) {}
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    setLoading(true);
    setError('');

    // Step 1 — authenticate against the API. Only errors from this call
    // should surface to the user; anything after a successful login
    // (analytics, biometric save, navigation) must be non-fatal because
    // the session is already valid on the server and stored locally.
    let response;
    try {
      console.log('[Login] API URL:', API);
      response = await httpClient.post(`${API}/api/auth/login-email`, {
        email,
        password,
      });
    } catch (err) {
      console.error('[Login] Error:', err);
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        setError(`Errore di rete. Server: ${API}`);
      } else if (err.code === 'ECONNABORTED') {
        setError('Timeout - il server non risponde');
      } else {
        setError(err.response?.data?.detail || err.message || 'Errore durante il login');
      }
      setLoading(false);
      return;
    }

    // Step 2 — post-success side effects. Each is wrapped so that a quirky
    // iOS WebKit error ("The object can not be cloned." is one we've seen
    // from the Capacitor bridge or biometric plugin) cannot block the user.
    try {
      if (response?.data?.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
      }
    } catch (_) { /* ignore */ }

    try { trackLogin('email'); } catch (_) { /* ignore */ }

    try {
      if (isNative && biometricAvailable) {
        await biometric.saveCredentials(email, password);
      }
    } catch (_) { /* ignore */ }

    const userData = response?.data?.user;
    try {
      if (userData && userData.is_admin === true) {
        localStorage.setItem('pending_admin_redirect', 'true');
        setUser(userData);
        setTimeout(() => {
          try { window.location.replace('/admin'); } catch (_) {}
        }, 100);
      } else {
        setUser(userData);
        const returnUrl = searchParams.get('returnUrl') || '/app/dashboard';
        navigate(returnUrl, { replace: true });
      }
    } catch (err) {
      // Final safety net: session is valid, so force a hard reload instead
      // of leaving the user stranded on the login page with a cryptic error.
      console.warn('[Login] Post-login navigation failed, forcing reload:', err?.message);
      try { window.location.replace('/app/dashboard'); } catch (_) {}
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      trackLogin('google');
      if (isNative) {
        // System browser (Custom Tab / SFSafariViewController) — Google blocks
        // OAuth inside webviews. Return handled by the slimmy:// deep link.
        await browser.open(`${API}/api/auth/google/login?source=app`);
      } else {
        window.location.href = `${API}/api/auth/google/login`;
      }
    } catch (err) {
      console.error('[GoogleLogin] EmailLogin failed:', err);
      setError(t('auth.googleLoginError') || 'Errore durante il login con Google. Riprova.');
    }
  };

  return (
    <div className="auth-page">
      {/* Background image - full screen coverage */}
      <img 
        src={SPLASH_IMAGE} 
        alt="" 
        className="auth-page-bg auth-mobile-only"
        style={{ filter: 'brightness(0.7)' }}
      />
      <img 
        src={DESKTOP_BG_IMAGE} 
        alt="" 
        className="auth-page-desktop-bg"
        style={{ filter: 'brightness(0.7)' }}
      />
      {/* Gradient overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/90 z-[1]"
      />

      {/* Content with safe area padding */}
      <div className="auth-page-content">
        <div className="px-5 pb-6 w-full max-w-md mx-auto">
          {/* Glass card */}
          <div className="backdrop-blur-xl bg-black/25 rounded-3xl p-6 border border-white/10 shadow-2xl">
            {/* Logo */}
            <div className="text-center mb-5">
              <img 
                src={LOGO_LIGHT} 
                alt="Slimmy"
                className="h-9 mx-auto mb-1.5 drop-shadow-lg"
              />
              <p className="text-white/90 text-sm font-medium">
                {t('auth.loginTitle')}
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* Email Input */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="login-email-input"
                  required
                />
              </div>

              {/* Password Input */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password')}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50 focus:border-transparent focus:bg-white/15"
                  data-testid="login-password-input"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="bg-red-500/20 text-red-200 p-3 rounded-xl text-sm text-center border border-red-500/30">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!email || !password || loading}
                className={`w-full py-3.5 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  email && password && !loading
                    ? 'bg-[#E8334A] text-white active:scale-[0.98]'
                    : 'bg-white/20 text-white/50 cursor-not-allowed'
                }`}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  t('auth.login')
                )}
              </button>
            </form>

            {/* Biometric login button (native only) */}
            {biometricAvailable && (
              <>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <span className="text-white/50 text-sm">{t('auth.or', 'oppure')}</span>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>
                <button
                  onClick={handleBiometricLogin}
                  disabled={biometricLoading}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-white/10 border border-white/20 text-white flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                  data-testid="biometric-login-btn"
                >
                  {biometricLoading ? (
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      {biometricType === 'faceId' ? <ScanFace size={22} /> : <Fingerprint size={22} />}
                      {biometricType === 'faceId' ? 'Face ID' : biometricType === 'touchId' ? 'Touch ID' : t('auth.fingerprint', 'Impronta digitale')}
                    </>
                  )}
                </button>
              </>
            )}

            {/* Google login option */}
            {googleLoginEnabled && (
              <>
                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-white/20"></div>
                  <span className="text-white/50 text-sm">{t('auth.or')}</span>
                  <div className="flex-1 h-px bg-white/20"></div>
                </div>
                
                <button
                  onClick={handleGoogleLogin}
                  className="w-full py-3.5 rounded-2xl font-semibold bg-white text-gray-800 flex items-center justify-center gap-3 active:scale-[0.98] transition-all"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {t('auth.googleLogin')}
                </button>
              </>
            )}

            {/* Register link */}
            <p className="text-white/60 text-sm mt-4 text-center">
              {t('auth.noAccount')}{' '}
              <button 
                onClick={() => navigate('/registrati')}
                className="text-white font-medium hover:underline"
              >
                {t('auth.register')}
              </button>
            </p>

            {/* Forgot Password */}
            <p className="text-white/50 text-sm mt-3 text-center">
              {t('auth.forgotPassword')}{' '}
              <button 
                onClick={() => navigate('/forgot-password')}
                className="text-white/70 hover:text-white hover:underline"
              >
                {t('auth.recoverHere')}
              </button>
            </p>

            {/* Back to phone login — hidden when admin disabled phone auth */}
            {phoneLoginEnabled && (
              <div className="pt-2">
                <button
                  onClick={() => navigate('/app/login?mode=phone')}
                  className="w-full text-center text-white/50 text-sm hover:text-white/70 flex items-center justify-center gap-1.5"
                  data-testid="phone-login-link"
                >
                  <Phone size={14} />
                  {t('auth.loginWithPhoneTitle') || 'Accedi con telefono'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
